import { useEffect, useState, useCallback, useRef } from 'react';
import { logger } from '@/lib/utils/logger';

interface SessionStorageOptions<T> {
  key: string;
  initialValue: T;
  expirationDays?: number;
  version?: string;
  enabled?: boolean;
  debounceMs?: number;
}

interface StoragePayload<T> {
  data: T;
  timestamp: number;
  version: string;
}

/**
 * Custom hook for persisting state to sessionStorage with auto-save and restore
 *
 * @param options - Configuration options
 * @returns Tuple of [value, setValue, clearStorage, isRestored]
 *
 * @example
 * ```tsx
 * const [filters, setFilters, clearFilters] = useSessionStorage({
 *   key: 'crm-filters',
 *   initialValue: defaultFilters,
 *   expirationDays: 7,
 *   version: '1.0',
 *   debounceMs: 500
 * });
 * ```
 */
export function useSessionStorage<T>({
  key,
  initialValue,
  expirationDays = 7,
  version = '1.0',
  enabled = true,
  debounceMs = 500,
}: SessionStorageOptions<T>) {
  const [value, setValue] = useState<T>(initialValue);
  const [isRestored, setIsRestored] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const initialValueRef = useRef(initialValue);

  // Restore from sessionStorage on mount
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setIsRestored(true);
      return;
    }

    try {
      const savedData = sessionStorage.getItem(key);
      if (!savedData) {
        setIsRestored(true);
        return;
      }

      const payload: StoragePayload<T> = JSON.parse(savedData);

      // Check version compatibility
      if (payload.version !== version) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[useSessionStorage] Version mismatch for key "${key}". Clearing old data.`);
        }
        sessionStorage.removeItem(key);
        setIsRestored(true);
        return;
      }

      // Check expiration
      const now = Date.now();
      const age = now - payload.timestamp;
      const maxAge = expirationDays * 24 * 60 * 60 * 1000;

      if (age > maxAge) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[useSessionStorage] Data expired for key "${key}". Clearing old data.`);
        }
        sessionStorage.removeItem(key);
        setIsRestored(true);
        return;
      }

      // Restore valid data
      setValue(payload.data);
      logger.dev(`[useSessionStorage] Restored data for key "${key}"`);
    } catch (error) {
      logger.error(`[useSessionStorage] Error restoring data for key "${key}":`, error);
      sessionStorage.removeItem(key);
    } finally {
      setIsRestored(true);
    }
  }, [key, version, expirationDays, enabled]);

  // Save to sessionStorage with debouncing
  useEffect(() => {
    if (!enabled || !isRestored || typeof window === 'undefined') {
      return;
    }

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save operation
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const payload: StoragePayload<T> = {
          data: value,
          timestamp: Date.now(),
          version,
        };
        sessionStorage.setItem(key, JSON.stringify(payload));
      } catch (error) {
        console.error(`[useSessionStorage] Error saving data for key "${key}":`, error);
      }
    }, debounceMs);

    // Cleanup timeout on unmount or value change
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [value, key, version, enabled, isRestored, debounceMs]);

  // Clear storage manually
  const clearStorage = useCallback(() => {
    if (typeof window === 'undefined') return;

    sessionStorage.removeItem(key);
    setValue(initialValueRef.current);
  }, [key]);

  return [value, setValue, clearStorage, isRestored] as const;
}
